import React from 'react';
import ResizeDetector from 'react-resize-detector';
import {
	Tabs as MuiTabs,
	type TabsProps as MuiTabsProps,
	type TabsActions,
} from '@mui/material';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {styled} from '@netx/core-components/Theme';
import {combineRefs} from '@netx/core-components/util';
import {useTabsProvider} from './TabsProvider';

const TabsRoot = styled(MuiTabs, {
	slot: 'Root',
})(({theme}) => ({
	flex: '0 0 auto',
}));

export interface TabsProps extends Omit<MuiTabsProps, 'onChange'> {
	// Overriding to enforce `number` as tab value
	onChange?: (event: React.SyntheticEvent, value: number) => void;
}

export const TabsPrimitive = (
	{action, orientation = 'horizontal', ...props}: TabsProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const tabs = useTabsProvider();
	const actionRef = useSharedRef<TabsActions | null>(null, [action]);

	return (
		<ResizeDetector<HTMLDivElement>
			handleWidth={orientation === 'horizontal'}
			handleHeight={orientation === 'vertical'}
			onResize={() => {
				actionRef.current?.updateIndicator();
			}}
		>
			{({targetRef}) => (
				<TabsRoot
					centered={props.variant !== 'scrollable'}
					indicatorColor="primary"
					textColor="primary"
					onChange={tabs.handleChange}
					value={tabs.activeTabIndex}
					{...props}
					orientation={orientation}
					action={actionRef}
					ref={combineRefs(ref, targetRef)}
				/>
			)}
		</ResizeDetector>
	);
};

/**
 * Wrapper that provides some common overrides, corrects for when
 * the contents resize, and accepts all the same props as the component
 * it wraps.<br/>
 * Supports automatic handling of active tab and tab change event if wrapped in a `TabsProvider`.
 */
export const Tabs = React.forwardRef(TabsPrimitive) as (
	props: TabsProps,
) => JSX.Element;

export default Tabs;
